import {
  SubCategoryRepository,
  CreateSubCategoryInput,
  UpdateSubCategoryInput,
} from '../../domain/repositories/sub-category.repository';
import { SubCategoryEntity, SubCategoryProps } from '../../domain/entities/sub-category.entity';
import { IHttpClient } from '../../application/interfaces/http-client.interface';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export class HttpSubCategoryRepository implements SubCategoryRepository {
  constructor(private readonly httpClient: IHttpClient) {}

  public async getSubCategories(options?: {
    activeOnly?: boolean;
    categoryId?: string;
    search?: string;
  }): Promise<SubCategoryEntity[]> {
    const endpoint = options?.activeOnly ? '/sub-categories/public' : '/sub-categories';
    const params = new URLSearchParams();

    if (options?.categoryId?.trim()) {
      params.append('categoryId', options.categoryId.trim());
    }

    if (options?.search?.trim()) {
      params.append('search', options.search.trim());
    }

    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await this.httpClient.get<ApiResponse<SubCategoryProps[]>>(`${endpoint}${query}`);

    if (!res.success || !res.data) {
      return [];
    }

    return res.data.map((item) => new SubCategoryEntity(item));
  }

  public async getSubCategoryById(id: string): Promise<SubCategoryEntity | null> {
    const res = await this.httpClient.get<ApiResponse<SubCategoryProps>>(`/sub-categories/${encodeURIComponent(id)}`);
    if (!res.success || !res.data) {
      return null;
    }
    return new SubCategoryEntity(res.data);
  }

  public async getSubCategoryBySlug(slug: string): Promise<SubCategoryEntity | null> {
    const res = await this.httpClient.get<ApiResponse<SubCategoryProps>>(`/sub-categories/slug/${encodeURIComponent(slug)}`);
    if (!res.success || !res.data) {
      return null;
    }
    return new SubCategoryEntity(res.data);
  }

  public async createSubCategory(data: CreateSubCategoryInput): Promise<SubCategoryEntity> {
    const res = await this.httpClient.post<ApiResponse<SubCategoryProps>>('/sub-categories', data);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al crear la subcategoría');
    }
    return new SubCategoryEntity(res.data);
  }

  public async updateSubCategory(id: string, data: UpdateSubCategoryInput): Promise<SubCategoryEntity> {
    const res = await this.httpClient.put<ApiResponse<SubCategoryProps>>(
      `/sub-categories/${encodeURIComponent(id)}`,
      data
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al actualizar la subcategoría');
    }
    return new SubCategoryEntity(res.data);
  }

  public async toggleSubCategoryStatus(id: string, status: boolean): Promise<SubCategoryEntity> {
    const res = await this.httpClient.patch<ApiResponse<SubCategoryProps>>(
      `/sub-categories/${encodeURIComponent(id)}/status`,
      { status }
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al cambiar estado de la subcategoría');
    }
    return new SubCategoryEntity(res.data);
  }

  public async deleteSubCategory(id: string): Promise<void> {
    const res = await this.httpClient.delete<ApiResponse<null>>(
      `/sub-categories/${encodeURIComponent(id)}`
    );
    if (!res.success) {
      throw new Error(res.message || 'Error al eliminar la subcategoría');
    }
  }
}
